import { formatNumber } from '@angular/common';
import { EchartsOption } from '@src/app/shared/directives/echarts.directive';
import { isMobileBrowser } from '@src/app/shared/utils/browser';
import { formatNumberToKMB, formatNumberWithPrefix } from '@src/app/shared/utils/number.util';
import dayjs from 'dayjs';

export const COMMON_TOOLTIP = {
    backgroundColor: 'rgba(0,0,0,0.85)',
    borderRadius: 8,
    borderWidth: 0,
    textStyle: { color: '#fff', fontSize: 12 },
};

export const CHART_TOOLTIP = {
    ...COMMON_TOOLTIP,
    trigger: 'axis',
    axisPointer: {
        type: 'line',
        label: { show: false },
        z: 0,
        lineStyle: {
            color: 'rgba(0,0,0,0.1)',
            type: 'solid',
        },
    },
};

export const initialOption: EchartsOption = {
    title: { show: false },
    backgroundColor: 'transparent',
    tooltip: {
        ...CHART_TOOLTIP,
        formatter: (params: any) => {
            return `<div style="text-align:left;"> ${dayjs(params[0].value[0]).format('DD MMM YYYY HH:mm')} <br/> Price: $${formatNumber(
                params[0].value[1],
                'en-us',
                '1.0-6'
            )} </div>`;
        },
    },
    legend: { show: false },
    grid: {
        top: 2,
        left: 2,
        right: 4,
        bottom: 4,
        containLabel: false,
    },
    xAxis: { show: false, type: 'time' },
    yAxis: { show: false },
};

export const volumeChartInitialOption: EchartsOption = {
    backgroundColor: 'transparent',
    tooltip: {
        ...CHART_TOOLTIP,
        trigger: 'axis',
        axisPointer: {
            type: 'shadow',
            z: 0,
            lineStyle: { color: 'rgba(0,0,0,0.1)' },
        },
        formatter: (params: any) => {
            if (!params || !params.length) return '';
            // 假设 params 顺序: 0: Retail %, 1: Total, 2: Insider, 3: Retail
            const date = dayjs(params[0].axisValue).format('MMM DD, YYYY HH:mm');
            let retailPercent = params.find((p: any) => p.seriesName.includes('%'));
            let insider = params.find((p: any) => p.seriesName.includes('Insider'));
            let retail = params.find((p: any) => p.seriesName.includes('Retail Trading Volume'));
            let total = (insider?.value || 0) + (retail?.value || 0);

            // 颜色
            const colorBox = (color: string) =>
                `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color};margin-right:6px;vertical-align:middle;"></span>`;
            return `
                <div style="min-width:160px; color:#999; font-size:10px;">
                    <div style="font-size:12px;font-weight:500;">${date}</div>
                    <div style="display:flex;align-items:center;justify-content:space-between;margin-top:4px;">
                        <span>
                            <span style="display:inline-block;width:18px;height:2px;background:#FFF9E5;margin-right:8px;vertical-align:middle;"></span> 
                            <span>Retail %</span>
                        </span>
                        <span style="font-size:12px;color:#fff;">${retailPercent ? retailPercent.value + '%' : '-'}</span>
                    </div>

                    <div style="height:1px; background:rgba(255,255,255,0.2); margin:8px 0;"></div>
                    <div style="margin:6px 0 0 0;">
                        <div style="display:flex;align-items:center;justify-content:flex-start;">
                            <span>${colorBox(insider?.color || '#7B8BFF')}</span>
                            <span>${colorBox(retail?.color || '#FFD600')}</span>
                            <span>Total Volume</span>
                            <span style="color:#fff;font-size:12px;flex:1; text-align:right;">$${total ? formatNumberToKMB(total) : '-'}</span>
                        </div>
                        <div style="display:flex;align-items:center;justify-content:flex-start;margin-top:2px;">
                            <span>${colorBox(insider?.color || '#7B8BFF')}</span>
                            <span>Insider Volume</span>
                            <span style="color:#fff;font-size:12px;flex:1; text-align:right;">$${insider ? formatNumberToKMB(insider.value) : '-'}</span>
                        </div>
                        <div style="display:flex;align-items:center;justify-content:flex-start;margin-top:2px;">
                            <span>${colorBox(retail?.color || '#FFD600')}</span>
                            <span>Retail Volume</span>
                            <span style="color:#fff;font-size:12px;flex:1; text-align:right;">$${retail ? formatNumberToKMB(retail.value) : '-'}</span>
                        </div>
                    </div>
                </div>
            `;
        },
    },
    legend: {
        data: [{ name: 'Retail Trading Volume Participation Rate %' }, { name: 'Insider Trading Volume' }, { name: 'Retail Trading Volume' }],
        bottom: 10,
        left: 'center',
        itemWidth: 18,
        itemHeight: 8,
        itemGap: 32,
    },
    xAxis: {
        type: 'category',
        data: [],
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
    },
    yAxis: [
        {
            type: 'value',
            axisLabel: { color: '#000', fontSize: 10, formatter: (value: number) => `$${formatNumberToKMB(value)}` },
            splitLine: { lineStyle: { color: '#eee' } },
        },
        {
            type: 'value',
            min: 0,
            axisLabel: { color: '#000', fontSize: 10 },
            splitLine: { show: false },
        },
    ],
    series: [],
};
